/**
 * GenerateAmcInvoiceNowDialog (Iter 37 + Iter 37.1)
 *
 * Manually trigger AMC invoice generation. Defaults to the scheduler's next
 * due period; operator can override the period to bill any window within
 * the contract (Iter 37.1, options a1/b1/c1/d1).
 *
 * - a1: period MUST stay within schedule.start_date..end_date.
 * - b1: per-period line amounts stay fixed (billing cycles are commercial
 *       constants, NOT pro-rated by days).
 * - c1: dialog defaults to the scheduler's next period (operator overrides).
 * - d1: next_due_date only advances when the chosen period covers it
 *       (enforced server-side in scheduler.persist_amc_invoice).
 *
 * Backend: POST /api/billing/amc/{schedule_id}/generate-invoice-now
 *   body: { period_start?: "YYYY-MM-DD", period_end?: "YYYY-MM-DD" }
 * RBAC: super_admin + accounts
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lightning, Eye, Calendar, Warning, ArrowsClockwise } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatINR, formatDate } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { DialogFooter } from "./ui/dialog";
import FormDialog, { FormDialogCancel } from "./FormDialog";
import { toast } from "sonner";

export default function GenerateAmcInvoiceNowDialog({ open, onOpenChange, sched, onGenerated }) {
    const [preview, setPreview] = useState(null);
    const [reason, setReason] = useState(null);   // server-side validation / overlap message
    const [conflict, setConflict] = useState(null);  // populated when preview overlaps existing invoice
    const [loading, setLoading] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [busy, setBusy] = useState(false);
    const [periodStart, setPeriodStart] = useState("");
    const [periodEnd, setPeriodEnd] = useState("");
    const [touched, setTouched] = useState(false);   // operator manually edited the period
    const navigate = useNavigate();
    const debounceRef = useRef(null);
    const initialPeriodRef = useRef({ start: "", end: "" });

    // Fetch the default (scheduler's next) preview when the dialog opens
    useEffect(() => {
        if (!open || !sched?.id) {
            setPreview(null);
            setReason(null);
            setConflict(null);
            setPeriodStart("");
            setPeriodEnd("");
            setTouched(false);
            return;
        }
        setLoading(true);
        setPreview(null);
        setReason(null);
        setConflict(null);
        (async () => {
            try {
                const { data } = await api.get(`/billing/amc/${sched.id}/preview-next-invoice`);
                setPreview(data.preview);
                setReason(data.reason);
                const ps = data.preview?.service_period_start || "";
                const pe = data.preview?.service_period_end || "";
                setPeriodStart(ps);
                setPeriodEnd(pe);
                initialPeriodRef.current = { start: ps, end: pe };
            } catch (err) {
                toast.error(formatApiError(err, "Failed to load preview"));
            } finally {
                setLoading(false);
            }
        })();
    }, [open, sched?.id]);

    // Iter 37.1: when operator edits the period, debounce-refetch the preview
    // so the dialog shows the live service-period subline + any overlap.
    useEffect(() => {
        if (!open || !sched?.id) return;
        // Skip on initial load when start/end match the initial preview values
        if (!touched) return;
        if (!periodStart || !periodEnd) {
            setReason("Both period start and end are required.");
            setPreview(null);
            return;
        }
        if (periodEnd < periodStart) {
            setReason("Period end must be on or after period start.");
            return;
        }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setPreviewing(true);
            try {
                const { data } = await api.get(
                    `/billing/amc/${sched.id}/preview-next-invoice`,
                    { params: { period_start: periodStart, period_end: periodEnd } },
                );
                setPreview(data.preview);
                setReason(data.reason);
                setConflict(data.conflict || null);
            } catch (err) {
                setReason(formatApiError(err, "Failed to refresh preview"));
            } finally {
                setPreviewing(false);
            }
        }, 350);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [periodStart, periodEnd, touched, open, sched?.id]);

    const resetPeriod = () => {
        setPeriodStart(initialPeriodRef.current.start);
        setPeriodEnd(initialPeriodRef.current.end);
        setTouched(false);
        // Trigger a clean refetch
        if (sched?.id) {
            (async () => {
                try {
                    const { data } = await api.get(`/billing/amc/${sched.id}/preview-next-invoice`);
                    setPreview(data.preview);
                    setReason(data.reason);
                    setConflict(null);
                } catch (err) {
                    toast.error(formatApiError(err, "Failed to load preview"));
                }
            })();
        }
    };

    // Iter 37.1 + Iter 59: contract window remains visible as advisory
    // metadata but is NO LONGER a hard bound. Operators can bill any past
    // or post-end period for receivables / extension / reconciliation use
    // cases. We display a soft advisory note when the chosen period is
    // outside the schedule's start/end window — never a blocking error.
    const schedBounds = useMemo(() => ({
        min: (sched?.start_date || "").slice(0, 10),
        max: (sched?.end_date || "").slice(0, 10),
    }), [sched?.start_date, sched?.end_date]);

    const isDirty = useMemo(
        () => touched && (
            periodStart !== initialPeriodRef.current.start ||
            periodEnd !== initialPeriodRef.current.end
        ),
        [touched, periodStart, periodEnd],
    );

    // Final guard: only LOCAL invalidity (end < start) and SERVER overlap
    // block Confirm. Out-of-bounds is an ADVISORY note, not a block.
    const hasBlockingError = !!reason && !!conflict;  // overlap → block
    const localInvalid = !!(touched && periodStart && periodEnd && periodEnd < periodStart);
    const outOfBoundsAdvisory = !!(touched && (
        (schedBounds.min && periodStart && periodStart < schedBounds.min) ||
        (schedBounds.max && periodEnd && periodEnd > schedBounds.max)
    ));
    const canGenerate = !!preview && !loading && !previewing && !busy
        && !hasBlockingError && !localInvalid && !!periodStart && !!periodEnd;

    const generate = async () => {
        if (!sched?.id || !preview) return;
        setBusy(true);
        try {
            // Always send the period (custom OR scheduler default) so the
            // backend has a deterministic record of what was billed.
            const body = { period_start: periodStart, period_end: periodEnd };
            const { data } = await api.post(`/billing/amc/${sched.id}/generate-invoice-now`, body);
            toast.success(`Invoice ${data.invoice_number} generated · ${formatINR(data.total_amount)}`);
            onGenerated?.(data);
            onOpenChange(false);
            if (data.invoice_id) navigate(`/invoices/${data.invoice_id}`);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to generate invoice"));
        } finally {
            setBusy(false);
        }
    };

    if (!sched) return null;

    return (
        <FormDialog
            open={open}
            onOpenChange={(v) => !busy && onOpenChange(v)}
            isDirty={isDirty}
            contentClassName="max-w-2xl max-h-[90vh] overflow-y-auto"
            testId="generate-amc-invoice-dialog"
            srTitle="Generate AMC invoice now"
            header={
                <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-lg font-semibold">
                        <Lightning size={18} weight="duotone" className="text-amber-600" />
                        Generate Invoice Now
                    </div>
                    <div className="text-xs text-slate-500">
                        Manually persist the next AMC invoice — same shape, audit, and immutability as the scheduler.
                        Defaults to the scheduler's next period; you can <b className="text-slate-700">override</b> to bill
                        any past-due, extension, or reconciliation period (Iter 59 — calendar end-date is advisory only).
                    </div>
                </div>
            }
        >
            <div className="space-y-4 py-2" data-testid="generate-amc-body">
                <div className="text-sm">
                    <div className="text-xs text-slate-500">AMC</div>
                    <div className="font-medium text-slate-900">{sched.amc_name || sched.product_name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{sched.client?.name}</div>
                    {(schedBounds.min || schedBounds.max) && (
                        <div className="text-[11px] text-slate-400 mt-1">
                            Contract window: {formatDate(schedBounds.min)} → {formatDate(schedBounds.max)}
                        </div>
                    )}
                </div>

                {/* Iter 37.1: editable billing period */}
                <div className="rounded-md border border-slate-200 bg-white px-4 py-3" data-testid="generate-amc-period-editor">
                    <div className="flex items-center justify-between gap-3 mb-2">
                        <div>
                            <div className="reedos-eyebrow flex items-center gap-1.5 text-[10px]">
                                <Calendar size={12} weight="duotone" /> <span>Billing period</span>
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                                Defaults to the scheduler's next period. Edit to bill any past-due / extension /
                                reconciliation window — overlap with an existing invoice is blocked.
                            </div>
                        </div>
                        {touched && (
                            <button
                                type="button"
                                onClick={resetPeriod}
                                className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 shrink-0"
                                data-testid="generate-amc-reset-period"
                                title="Reset to the scheduler's next period"
                            >
                                <ArrowsClockwise size={11} /> Reset
                            </button>
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[11px] reedos-eyebrow text-slate-600">Period start *</Label>
                            <Input
                                type="date"
                                value={periodStart}
                                onChange={(e) => { setPeriodStart(e.target.value); setTouched(true); }}
                                className="mt-1"
                                data-testid="generate-amc-period-start"
                            />
                        </div>
                        <div>
                            <Label className="text-[11px] reedos-eyebrow text-slate-600">Period end *</Label>
                            <Input
                                type="date"
                                value={periodEnd}
                                onChange={(e) => { setPeriodEnd(e.target.value); setTouched(true); }}
                                className="mt-1"
                                data-testid="generate-amc-period-end"
                            />
                        </div>
                    </div>
                    {localInvalid && (
                        <div className="mt-2 text-xs text-rose-700" data-testid="generate-amc-period-local-error">
                            Period end must be on or after period start.
                        </div>
                    )}
                    {!localInvalid && outOfBoundsAdvisory && (
                        <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 flex items-start gap-1.5" data-testid="generate-amc-period-advisory">
                            <Warning size={12} weight="fill" className="mt-0.5 text-amber-600 shrink-0" />
                            <span>
                                Advisory: this period is outside the schedule's contract window
                                ({formatDate(schedBounds.min)} → {formatDate(schedBounds.max)}). The
                                schedule remains billable per Iter 59 — proceed if this is a past-due
                                / extension / reconciliation invoice.
                            </span>
                        </div>
                    )}
                </div>

                {(loading || previewing) && (
                    <div className="text-xs text-slate-500" data-testid="generate-amc-loading">
                        {previewing ? "Refreshing preview…" : "Building preview…"}
                    </div>
                )}

                {!loading && !preview && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2" data-testid="generate-amc-blocked">
                        <Warning size={16} weight="fill" className="mt-0.5 text-amber-600 shrink-0" />
                        <div>
                            <div className="font-semibold">Cannot generate right now</div>
                            <div className="text-xs mt-0.5">{reason || "No upcoming invoice can be previewed."}</div>
                        </div>
                    </div>
                )}

                {/* Conflict callout — preview is shown but generate is blocked */}
                {!loading && preview && conflict && (
                    <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900 flex items-start gap-2" data-testid="generate-amc-overlap-warning">
                        <Warning size={16} weight="fill" className="mt-0.5 text-rose-600 shrink-0" />
                        <div>
                            <div className="font-semibold">Overlap with existing invoice</div>
                            <div className="text-xs mt-0.5">
                                {conflict.invoice_number || conflict.id} ({formatDate(conflict.billing_start_date)} → {formatDate(conflict.billing_end_date)}) already covers this period. Pick a different range to continue.
                            </div>
                        </div>
                    </div>
                )}

                {!loading && preview && (
                    <>
                        <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
                            <table className="w-full text-xs">
                                <thead className="bg-slate-50 border-b border-slate-200">
                                    <tr className="text-left">
                                        <th className="px-3 py-2 reedos-eyebrow w-[44%]">Description</th>
                                        <th className="px-3 py-2 reedos-eyebrow text-right">Amount</th>
                                        <th className="px-3 py-2 reedos-eyebrow text-right">GST</th>
                                        <th className="px-3 py-2 reedos-eyebrow text-right">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {preview.line_items.map((li, idx) => (
                                        <tr key={idx} className="border-t border-slate-100 align-top">
                                            <td className="px-3 py-2 text-slate-900">
                                                <div className="font-medium">{li.description}</div>
                                                {li.is_amc_period && (
                                                    <div className="text-[11px] text-slate-500 mt-0.5">
                                                        <div>{li.service_charge_label}</div>
                                                        <div>Service Period: {formatDate(li.service_period_start)} → {formatDate(li.service_period_end)}</div>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-right reedos-data">{formatINR(li.amount)}</td>
                                            <td className="px-3 py-2 text-right reedos-data text-slate-600">
                                                {formatINR(li.gst_amount)}
                                                <span className="text-[10px] text-slate-400 ml-1">({li.gst_percent}%)</span>
                                            </td>
                                            <td className="px-3 py-2 text-right reedos-data font-semibold">{formatINR(li.total_amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="border-t border-slate-200 bg-slate-50/50">
                                    <tr>
                                        <td className="px-3 py-2 reedos-eyebrow text-right" colSpan={3}>Subtotal</td>
                                        <td className="px-3 py-2 reedos-data text-right">{formatINR(preview.subtotal)}</td>
                                    </tr>
                                    <tr>
                                        <td className="px-3 py-2 reedos-eyebrow text-right" colSpan={3}>GST</td>
                                        <td className="px-3 py-2 reedos-data text-right">{formatINR(preview.tax_amount)}</td>
                                    </tr>
                                    <tr className="border-t border-slate-200">
                                        <td className="px-3 py-2 font-semibold text-right text-slate-900" colSpan={3}>Total payable</td>
                                        <td className="px-3 py-2 reedos-data text-right font-bold text-slate-900" data-testid="generate-amc-total">{formatINR(preview.total_amount)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        <div className="flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
                            <span className="inline-flex items-center gap-1">
                                <Calendar size={11} /> Period {formatDate(preview.service_period_start)} → {formatDate(preview.service_period_end)}
                            </span>
                            <span>·</span>
                            <span>Payment terms: <b className="text-slate-700">{preview.payment_terms}</b></span>
                            <span>·</span>
                            <span>Frequency: <b className="text-slate-700">{preview.billing_frequency}</b></span>
                            {touched && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200" data-testid="generate-amc-custom-period-chip">
                                    Custom period
                                </span>
                            )}
                        </div>

                        <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2 text-[11px] text-slate-600 flex items-start gap-2">
                            <Eye size={12} className="mt-0.5 text-slate-500 shrink-0" />
                            <span>
                                This is a <b>preview</b>. Confirming will persist the invoice with the exact same numbers
                                via the scheduler's persistence path. The action is audited and the resulting invoice is immutable.
                                {touched && " The schedule's next due date will only advance if this period covers it."}
                            </span>
                        </div>
                    </>
                )}
            </div>
            <DialogFooter className="border-t border-slate-100 -mx-6 px-6 pt-4 mt-2">
                <FormDialogCancel disabled={busy} testId="generate-amc-cancel-btn" />
                <Button
                    onClick={generate}
                    disabled={!canGenerate}
                    data-testid="generate-amc-confirm-btn"
                    className="bg-amber-600 hover:bg-amber-500 text-white gap-1.5"
                >
                    <Lightning size={14} weight="fill" />
                    {busy ? "Generating…" : "Generate invoice now"}
                </Button>
            </DialogFooter>
        </FormDialog>
    );
}
